import React, { useState } from 'react';
import { Paper, Container, Box, Tabs, Tab, CircularProgress, Typography, Button, Dialog, DialogTitle, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Alert, Grid } from '@mui/material';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Users, Code, CheckCircle, ArrowRight, Check, ClipboardList, BarChart, GitBranch, CheckCircle2 } from 'lucide-react';
import ClarityAnalytics from './clarity';

const NavigationHeader = ({ 
  currentTab, 
  handleTabChange, 
  creditsLoading, 
  credits,
  user,
  isLoaded
}) => {
  const [openPricing, setOpenPricing] = useState(false);
  const [openBADetails, setOpenBADetails] = useState(false);
  const [openBooking, setOpenBooking] = useState(false);
  const [openCustomizationDetails, setOpenCustomizationDetails] = useState(false);

  const creditPackages = [
    {
      title: "Developer Pro",
      subtitle: "Build More",
      amount: "5000 Credits",
      price: "$15 per month",
      tag: "Most Popular",
      tagColor: "#6366F1", // Indigo color
      features: [
        "Project Planning (500 credits)",
        "Code Generation and deploy (1500 credits)",
        "Code Download (500 credits)",
        "Priority Support",
        "Community Access",
        "30-day credit validity",
        "Recharge anytime"
      ]
    }
  ];

  const baServices = [
    {
      icon: <ClipboardList />,
      title: "Requirements Engineering",
      items: [
        "Comprehensive requirement gathering",
        "Gap analysis and documentation",
        "Business process modeling",
        "User story creation and refinement"
      ]
    },
    {
      icon: <Users />,
      title: "Stakeholder Collaboration",
      items: [
        "Stakeholder analysis and management",
        "Facilitation of requirements workshops",
        "Cross-functional team coordination",
        "Clear communication of technical concepts"
      ]
    },
    {
      icon: <BarChart />,
      title: "Process Optimization",
      items: [
        "Business process analysis",
        "Workflow optimization",
        "Performance metrics definition",
        "Efficiency improvement recommendations"
      ]
    },
    {
      icon: <GitBranch />,
      title: "Documentation & Deliverables",
      items: [
        "Software Requirements Specification (SRS)",
        "Business Requirements Document (BRD)",
        "Process flow diagrams",
        "User acceptance criteria"
      ]
    }
  ];

  return (
    <>
    <><ClarityAnalytics /></>
    <Paper elevation={3} className="z-10">
      <Container maxWidth="xl" className="py-2">
        <Box
          className="flex items-center justify-between"
          sx={{ pl: { xs: "48px", sm: "56px" } }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="min-h-[48px]"
            sx={{
              flex: 1,
              ml: { xs: 1, sm: 2 },
              "& .MuiTab-root": {
                minHeight: 56,
                fontSize: "1rem",
                fontWeight: 500,
                textTransform: "none",
                px: 4,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  color: "#1a1a1a",
                },
              },
              "& .Mui-selected": {
                backgroundColor: "#e0e0e0",
                color: "#1a1a1a",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1a1a1a",
              },
            }}
          >
            <Tab label="Project Description" />
            <Tab label="Requirements" />
            <Tab label="User Stories" />
            <Tab label="Sitemap" />
            <Tab label="Data Models" />
            <Tab label="Your web app" />
          </Tabs>
          <Box className="flex items-center gap-4 mr-4">
            <Box className="flex items-center gap-4 mr-4">
              <Box className="flex flex-col items-end">
                {creditsLoading ? (
                  <CircularProgress size={20} />
                ) : credits ? (
                  <>
                    <Typography
                      variant="body2"
                      className="text-gray-600"
                    >
                      Available Credits:{" "}
                      <span className="font-semibold text-gray-800">
                        {credits.amount}
                      </span>
                    </Typography>

                    {
                      credits.amount > 0 && (
                        <Typography
                        variant="caption"
                        className="text-gray-500"
                      >
                        Expires in{" "}
                        {Math.ceil(
                          (credits.expiryDate - Date.now()) /
                            (1000 * 60 * 60 * 24)
                        )}{" "}
                        days
                      </Typography>
                      )
                    }
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setOpenPricing(true)}
                      className="mt-1"
                      sx={{
                        backgroundColor: '#1a1a1a',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#333333',
                        }
                      }}
                    >
                      Get More Credits
                    </Button>

                  </>
                ) : (
                  <>
                    <Typography variant="body2" className="text-gray-600">
                      No credits available
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setOpenPricing(true)}
                      className="mt-1"
                      sx={{
                        backgroundColor: '#1a1a1a',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#333333',
                        }
                      }}
                    >
                      Get Credits
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          <UserButton />
        </Box>
      </Container>

      <Dialog 
        open={openPricing} 
        onClose={() => setOpenPricing(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 500, mb: 2 }}>Our Services & Pricing</DialogTitle>
        <DialogContent>
          <Grid container spacing={4}>
            {/* Credit Packages */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3 }}>Credit Packages</Typography>
              <Box className="grid grid-cols-1 gap-6 mb-8">
                {creditPackages.map((pkg) => (
                  <Card 
                    key={pkg.title} 
                    className="relative w-full"
                    sx={{ 
                      bgcolor: 'white',
                      color: 'black',
                      border: '1px solid #e0e0e0',
                      height: '100%'
                    }}
                  >
                    {pkg.tag && (
                      <Box 
                        className="absolute top-2 right-2 px-3 py-1 rounded-full"
                        sx={{ 
                          bgcolor: pkg.tagColor || '#6366F1',
                          fontSize: '0.875rem'
                        }}
                      >
                        {pkg.tag}
                      </Box>
                    )}
                    <CardContent className="p-6">
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>{pkg.subtitle}</Typography>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>{pkg.title}</Typography>
                      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>{pkg.amount}</Typography>
                      <Typography variant="h6" sx={{ mb: 4, color: 'gray.300' }}>{pkg.price}</Typography>
                      
                      <Box className="space-y-3">
                        {pkg.features.map((feature) => (
                          <Box key={feature} className="flex items-center gap-2">
                            <Check className="w-5 h-5" sx={{ color: '#22C55E' }} />
                            <Typography color="black">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {isLoaded && user && (
                        <Box
                          sx={{
                            mt: 4,
                            display: 'flex',
                            justifyContent: 'center',
                            "& stripe-buy-button": {
                              display: "block",
                              width: "100%",
                              maxWidth: "300px",
                              "--stripe-buy-button-theme-text-color": "#4f46e5",
                              "--stripe-buy-button-theme-background-color": "#ffffff",
                              "--stripe-buy-button-height": "40px",
                            },
                          }}
                        >
                          <stripe-buy-button
                            buy-button-id="buy_btn_1Qd0NTJNqQqG6YgGYMIs4XtP"
                            publishable-key="pk_live_51K9B4oJNqQqG6YgG62UgijNg3QYZkjGP9C2SycPnUgqsCdfMxU59WsrNtD7zaEeNBAQbNCCAnTsXWyqZ0l8uHKYX006OqqSHF0"
                            customer-email={user.primaryEmailAddress?.emailAddress}
                            customer-metadata={JSON.stringify({
                              clerkUserId: user.id,
                              email: user.primaryEmailAddress?.emailAddress,
                            })}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Professional Services */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3 }}>Professional Services</Typography>
              <Box className="grid grid-cols-1 gap-6 mb-8">
                {/* Business Analysis Service */}
                <Card className="relative" sx={{ bgcolor: 'white', color: 'black', border: '1px solid #e0e0e0' }}>
                  <Box className="absolute top-2 right-2" sx={{ 
                    bgcolor: '#22C55E',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 'full',
                    fontSize: '0.875rem'
                  }}>
                    Popular Choice
                  </Box>
                  <CardContent className="p-6">
                    <Box className="flex items-start gap-4">
                      <Box className="p-3" sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
                        <Users className="w-6 h-6" sx={{ color: '#6366F1' }} />
                      </Box>
                      <Box className="flex-1">
                        <Typography variant="h6" sx={{ mb: 1, color: 'black' }}>Business Analysis Service</Typography>
                        <Typography sx={{ mb: 2, color: 'gray.600' }}>
                          Expert BA consultation to define your project requirements
                        </Typography>
                        <Box className="space-y-2 mb-4">
                          {['Requirement gathering', 'Process optimization', 'Technical documentation'].map((feature) => (
                            <Box key={feature} className="flex items-center gap-2">
                              <Check className="w-4 h-4" sx={{ color: '#22C55E' }} />
                              <Typography>{feature}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box className="flex justify-between items-center">
                          <Typography variant="h6">Starting at $499</Typography>
                          <Button 
                            variant="contained"
                            onClick={() => setOpenBADetails(true)}
                            sx={{
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff',
                              '&:hover': {
                                backgroundColor: '#333333',
                              }
                            }}
                          >
                            Learn More
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* App Customization Service */}
                <Card className="relative" sx={{ bgcolor: 'white', color: 'black', border: '1px solid #e0e0e0' }}>
                  <Box className="absolute top-2 right-2" sx={{ 
                    bgcolor: '#6366F1',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 'full',
                    fontSize: '0.875rem'
                  }}>
                    Most Flexible
                  </Box>
                  <CardContent className="p-6">
                    <Box className="flex items-start gap-4">
                      <Box className="p-3" sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
                        <Code className="w-6 h-6" sx={{ color: '#6366F1' }} />
                      </Box>
                      <Box className="flex-1">
                        <Typography variant="h6" sx={{ mb: 1, color: 'black' }}>App Customization Service</Typography>
                        <Typography sx={{ mb: 2, color: 'gray.600' }}>
                          Tailored modifications to match your exact needs
                        </Typography>
                        <Box className="space-y-2 mb-4">
                          {['Custom features', 'UI/UX modifications', 'Integration support'].map((feature) => (
                            <Box key={feature} className="flex items-center gap-2">
                              <Check className="w-4 h-4" sx={{ color: '#22C55E' }} />
                              <Typography>{feature}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box className="flex justify-between items-center">
                          <Typography variant="h6">Starting at $999</Typography>
                          <Button 
                            variant="contained"
                            onClick={() => setOpenCustomizationDetails(true)}
                            sx={{
                              bgcolor: '#1a1a1a',
                              color: '#ffffff',
                              '&:hover': {
                                bgcolor: '#333333',
                              }
                            }}
                          >
                            Learn More
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* Call-to-Action Section */}
          <Alert 
            severity="info" 
            sx={{ 
              bgcolor: 'white',
              color: 'black',
              border: '1px solid #e0e0e0',
              '& .MuiAlert-icon': {
                color: '#6366F1'
              }
            }}
          >
            <Box className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-2 sm:p-4">
              <Box className="w-full sm:w-auto">
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 0.5,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Need Help?
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'gray.300',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Have questions or need assistance? Our support team is here to help.
                </Typography>
              </Box>
              <Button
                variant="contained"
                component="a"
                href="mailto:info@neopric.com"
                sx={{
                  bgcolor: '#1a1a1a',
                  color: '#ffffff',
                  minWidth: { xs: '100%', sm: '200px' },
                  '&:hover': {
                    bgcolor: '#333333',
                  }
                }}
                endIcon={<ArrowRight className="w-4 h-4" />}
              >
                Contact Support
              </Button>
            </Box>
          </Alert>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={openBADetails} 
        onClose={() => setOpenBADetails(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogContent sx={{ bgcolor: '#0A0A0A', color: 'white' }}>
          <Box className="p-6">
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              Business Analysis Service
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 6, color: 'gray.300' }}>
              Transform your business requirements into actionable technical solutions with our expert Business Analysis service. We bridge the gap between business needs and technology implementation through systematic requirement gathering, analysis, and documentation.
            </Typography>

            <Grid container spacing={4} sx={{ mb: 6 }}>
              {baServices.map((service) => (
                <Grid item xs={12} md={6} key={service.title}>
                  <Box className="flex gap-4">
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(99, 102, 241, 0.1)', 
                      borderRadius: 2,
                      color: '#6366F1',
                      height: 'fit-content'
                    }}>
                      {service.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {service.title}
                      </Typography>
                      <Box className="space-y-2">
                        {service.items.map((item) => (
                          <Box key={item} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                            <Typography variant="body2">{item}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ 
              p: 4, 
              bgcolor: 'rgba(99, 102, 241, 0.1)', 
              borderRadius: 2,
              mb: 4 
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Why Choose Our BA Service?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Proven methodology and best practices</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Experienced BA professionals</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Clear and structured documentation</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Agile-ready approach</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box className="flex justify-end gap-3">
              <Button
                variant="contained"
                size="large"
                component="a"
                href="mailto:info@neopric.com"
                onClick={() => setOpenBADetails(false)}
                sx={{
                  bgcolor: 'white',
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'gray.100',
                  }
                }}
              >
                Start Your Project
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={openCustomizationDetails} 
        onClose={() => setOpenCustomizationDetails(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogContent sx={{ bgcolor: '#0A0A0A', color: 'white' }}>
          <Box className="p-6">
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              App Customization Service
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 6, color: 'gray.300' }}>
              Get a tailored solution that perfectly matches your business requirements. Our expert developers will customize and extend the application to meet your specific needs.
            </Typography>

            <Box sx={{ 
              p: 4, 
              bgcolor: 'rgba(99, 102, 241, 0.1)', 
              borderRadius: 2,
              mb: 4 
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Service Features
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Custom feature development</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>UI/UX modifications</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Third-party integrations</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" sx={{ color: '#22C55E' }} />
                    <Typography>Technical support</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box className="flex justify-end gap-3">
              <Button
                variant="contained"
                size="large"
                component="a"
                href="mailto:info@neopric.com"
                onClick={() => setOpenCustomizationDetails(false)}
                sx={{
                  bgcolor: 'white',
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'gray.100',
                  }
                }}
              >
                Start Your Project
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
    </>
  );
};

export default NavigationHeader; 